const BECH32_ALPHABET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";

function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.startsWith("0x") ? hex.slice(2) : hex;

  if (normalized.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(normalized)) {
    throw new Error("Invalid hex string");
  }

  const bytes = new Uint8Array(normalized.length / 2);
  for (let index = 0; index < normalized.length; index += 2) {
    bytes[index / 2] = Number.parseInt(normalized.slice(index, index + 2), 16);
  }

  return bytes;
}

function convertBits(
  data: Uint8Array,
  fromBits: number,
  toBits: number,
  pad = true,
): number[] {
  let acc = 0;
  let bits = 0;
  const maxValue = (1 << toBits) - 1;
  const maxAcc = (1 << (fromBits + toBits - 1)) - 1;
  const result: number[] = [];

  for (const value of data) {
    if (value < 0 || value >> fromBits !== 0) {
      throw new Error("Invalid value while converting address bits");
    }

    acc = ((acc << fromBits) | value) & maxAcc;
    bits += fromBits;

    while (bits >= toBits) {
      bits -= toBits;
      result.push((acc >> bits) & maxValue);
    }
  }

  if (pad) {
    if (bits > 0) {
      result.push((acc << (toBits - bits)) & maxValue);
    }
    return result;
  }

  if (bits >= fromBits) {
    throw new Error("Excess padding while converting address bits");
  }

  if ((acc << (toBits - bits)) & maxValue) {
    throw new Error("Non-zero padding while converting address bits");
  }

  return result;
}

function polymod(values: number[]): number {
  const generators = [
    0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3,
  ];

  let chk = 1;
  for (const value of values) {
    const top = chk >>> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ value;
    for (let index = 0; index < generators.length; index += 1) {
      if ((top >>> index) & 1) {
        chk ^= generators[index];
      }
    }
  }

  return chk;
}

function hrpExpand(hrp: string): number[] {
  const result: number[] = [];
  for (let index = 0; index < hrp.length; index += 1) {
    result.push(hrp.charCodeAt(index) >> 5);
  }
  result.push(0);
  for (let index = 0; index < hrp.length; index += 1) {
    result.push(hrp.charCodeAt(index) & 31);
  }
  return result;
}

function createChecksum(hrp: string, data: number[]): number[] {
  const values = [...hrpExpand(hrp), ...data, 0, 0, 0, 0, 0, 0];
  const mod = polymod(values) ^ 1;
  const checksum: number[] = [];

  for (let index = 0; index < 6; index += 1) {
    checksum.push((mod >>> (5 * (5 - index))) & 31);
  }

  return checksum;
}

function encodeBech32(hrp: string, data: number[]): string {
  const combined = [...data, ...createChecksum(hrp, data)];
  let encoded = `${hrp}1`;

  for (const value of combined) {
    encoded += BECH32_ALPHABET[value];
  }

  return encoded;
}

function isBech32Address(value: string): boolean {
  return /^(addr|addr_test|stake|stake_test)1[0-9a-z]+$/i.test(value);
}

function getBech32PrefixFromAddressBytes(
  bytes: Uint8Array,
): "addr" | "addr_test" {
  // Cardano encodes the network id in the low nibble of the first byte.
  // Mainnet is 1; everything else we display here uses the testnet-style prefix.
  return (bytes[0] & 0x0f) === 1 ? "addr" : "addr_test";
}

export function formatCardanoAddress(
  address: string | null | undefined,
): string | null {
  if (!address) return null;

  const trimmed = address.trim();
  if (!trimmed) return null;
  if (isBech32Address(trimmed)) return trimmed;

  try {
    const bytes = hexToBytes(trimmed);
    if (bytes.length === 0) return null;
    const hrp = getBech32PrefixFromAddressBytes(bytes);
    return encodeBech32(hrp, convertBits(bytes, 8, 5, true));
  } catch {
    return trimmed;
  }
}

export function shortenCardanoAddress(
  address: string | null | undefined,
  head = 12,
  tail = 8,
): string | null {
  const formatted = formatCardanoAddress(address);
  if (!formatted) return null;
  if (formatted.length <= head + tail + 3) return formatted;
  return `${formatted.slice(0, head)}...${formatted.slice(-tail)}`;
}
